import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  Upload,
  Globe,
  Loader2,
  Image as ImageIcon,
  Check,
  Crop
} from 'lucide-react';
import { uploadImage } from '../../utils/api';
import { applyCdn } from '../../utils/mediaUrl';
import CropModal from '../admin/CropModal';

interface InsertImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (url: string, alt?: string) => void;
  initialFile?: File | null;
}

export default function InsertImageModal({
  isOpen,
  onClose,
  onInsert,
  initialFile
}: InsertImageModalProps) {
  const [imageTab, setImageTab] = useState<'upload' | 'url'>('upload');
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [useCdn, setUseCdn] = useState(true);
  const [uploadedPreview, setUploadedPreview] = useState<string | null>(null);
  const [isBoxDragOver, setIsBoxDragOver] = useState(false);

  // Image Editor / Cropper states
  const [cropImageSrc, setCropImageSrc] = useState<string>('');
  const [showCropModal, setShowCropModal] = useState(false);
  const [pendingRawFile, setPendingRawFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const processedFileRef = useRef<File | null>(null);

  // Automatically trigger image editor when an initial file is provided (e.g. from Drag & Drop)
  useEffect(() => {
    if (isOpen && initialFile && initialFile !== processedFileRef.current) {
      processedFileRef.current = initialFile;
      handlePickedFile(initialFile);
    }
  }, [isOpen, initialFile]);

  // Reset internal states on modal close
  useEffect(() => {
    if (!isOpen) {
      processedFileRef.current = null;
      setImageUrl('');
      setImageAlt('');
      setUploadedPreview(null);
      setCropImageSrc('');
      setShowCropModal(false);
      setPendingRawFile(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePickedFile = (file: File) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Silakan pilih berkas gambar (JPG, PNG, WebP).');
      return;
    }

    setPendingRawFile(file);
    if (!imageAlt) {
      const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      setImageAlt(cleanName);
    }

    // Read file as Data URL to launch the image editor (CropModal)
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handlePickedFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCropSave = async (croppedFile: File) => {
    setShowCropModal(false);
    setIsUploading(true);
    try {
      const uploadedPath = await uploadImage(croppedFile, 'generic');
      let finalSrc = uploadedPath;
      if (useCdn && finalSrc) {
        finalSrc = applyCdn(finalSrc);
      }
      setUploadedPreview(finalSrc);
      setImageUrl(finalSrc);
    } catch (err: any) {
      alert(err?.message || 'Gagal mengunggah gambar');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropClose = async () => {
    setShowCropModal(false);
    // If not yet uploaded and user closes crop without editing, upload the original file
    if (!uploadedPreview && pendingRawFile) {
      setIsUploading(true);
      try {
        const uploadedPath = await uploadImage(pendingRawFile, 'generic');
        let finalSrc = uploadedPath;
        if (useCdn && finalSrc) {
          finalSrc = applyCdn(finalSrc);
        }
        setUploadedPreview(finalSrc);
        setImageUrl(finalSrc);
      } catch (err: any) {
        alert(err?.message || 'Gagal mengunggah gambar');
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleConfirm = () => {
    let finalSrc = imageUrl.trim();
    if (useCdn && finalSrc) {
      finalSrc = applyCdn(finalSrc);
    }
    if (finalSrc) {
      onInsert(finalSrc, imageAlt.trim() || undefined);
    }
    handleClose();
  };

  const handleClose = () => {
    setImageUrl('');
    setImageAlt('');
    setUploadedPreview(null);
    setCropImageSrc('');
    setShowCropModal(false);
    setPendingRawFile(null);
    processedFileRef.current = null;
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-[9990] bg-black/70 backdrop-blur-xs flex items-center justify-center p-4">
        <div className="bg-white text-ink border border-gray-100 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-5 animate-in fade-in zoom-in-95">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-brand-wine/10 text-brand-wine flex items-center justify-center">
                <ImageIcon className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">Sisipkan Gambar ke Konten</h3>
                <p className="text-[11px] text-muted">Gambar akan ditempatkan di posisi kursor Anda</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-700 p-1.5 rounded-lg transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Segmented Tab */}
          <div className="flex rounded-xl bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setImageTab('upload')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                imageTab === 'upload' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <Upload className="w-3.5 h-3.5" />
              Unggah File Komputer
            </button>
            <button
              type="button"
              onClick={() => setImageTab('url')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                imageTab === 'url' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              URL Gambar / CDN
            </button>
          </div>

          {imageTab === 'upload' ? (
            <div className="space-y-3">
              {uploadedPreview ? (
                <div className="relative border border-border-subtle rounded-xl overflow-hidden bg-gray-50 p-2 space-y-2">
                  <img
                    src={uploadedPreview}
                    alt="Preview"
                    className="w-full h-44 object-contain rounded-lg bg-white border border-gray-100"
                  />
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setCropImageSrc(uploadedPreview);
                        setShowCropModal(true);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-xs font-bold transition-all cursor-pointer"
                      title="Edit / Potong Gambar"
                    >
                      <Crop className="w-3.5 h-3.5 text-brand-wine" />
                      <span>Edit / Crop</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUploadedPreview(null);
                        setImageUrl('');
                        setPendingRawFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-xs font-bold transition-all cursor-pointer"
                      title="Ganti gambar"
                    >
                      Ganti File
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isUploading) setIsBoxDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsBoxDragOver(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsBoxDragOver(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) handlePickedFile(file);
                  }}
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center gap-2.5 cursor-pointer transition-all ${
                    isBoxDragOver
                      ? 'border-brand-wine bg-brand-wine/10 ring-2 ring-brand-wine/20 scale-[1.01]'
                      : 'border-gray-200 hover:border-brand-wine/50 bg-gray-50/50'
                  }`}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-8 h-8 text-brand-wine animate-spin" />
                      <span className="text-xs text-gray-700 font-bold">Mengunggah ke server...</span>
                      <span className="text-[10px] text-muted">Mohon tunggu sebentar</span>
                    </>
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-full bg-brand-wine/10 text-brand-wine flex items-center justify-center">
                        <Upload className="w-6 h-6" />
                      </div>
                      <span className="text-xs font-bold text-gray-800">
                        {isBoxDragOver ? 'Lepaskan gambar di sini' : 'Klik atau seret gambar ke sini'}
                      </span>
                      <span className="text-[11px] text-gray-400">JPG, PNG, WebP hingga 5MB (Buka Editor Gambar)</span>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    disabled={isUploading}
                    className="hidden"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <label className="block text-xs font-bold text-gray-700">Tautan Gambar Langsung (URL)</label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => {
                  setImageUrl(e.target.value);
                  setUploadedPreview(e.target.value);
                }}
                placeholder="https://secera.id/uploads/... atau https://..."
                className="w-full px-3.5 py-2.5 bg-gray-50 border border-border-subtle rounded-xl text-xs outline-none focus:border-brand-wine focus:bg-white transition-all font-mono"
              />
            </div>
          )}

          {/* Alt Text input */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-gray-700">Alt Text (Deskripsi SEO & Aksesibilitas)</label>
            <input
              type="text"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
              placeholder="Contoh: Detail jahitan selendang instant bahan silk"
              className="w-full px-3.5 py-2.5 bg-gray-50 border border-border-subtle rounded-xl text-xs outline-none focus:border-brand-wine focus:bg-white transition-all"
            />
            <p className="text-[10px] text-muted">
              Membantu ranking di Google Images dan dibaca oleh screen reader tunanetra.
            </p>
          </div>

          {/* CDN Checkbox & Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={useCdn}
                onChange={(e) => setUseCdn(e.target.checked)}
                className="rounded text-brand-wine focus:ring-brand-wine accent-[#722F38]"
              />
              Optimalkan via Hostinger CDN
            </label>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-3.5 py-2 border border-gray-200 text-xs font-bold text-gray-600 rounded-xl hover:border-gray-800 transition-colors cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!imageUrl.trim() || isUploading}
                className="flex items-center gap-1.5 px-4 py-2 bg-brand-wine text-white text-xs font-bold rounded-xl hover:bg-brand-wine-deep disabled:opacity-40 transition-all cursor-pointer shadow-sm shadow-brand-wine/20"
              >
                <Check className="w-3.5 h-3.5" />
                Sisipkan Gambar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Embedded CropModal (Editor Gambar) */}
      <CropModal
        isOpen={showCropModal}
        onClose={handleCropClose}
        imageSrc={cropImageSrc}
        onCrop={handleCropSave}
        aspectRatio={NaN}
      />
    </>
  );
}
